import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Pill, Edit, Trash2, Save, X, Plus, ArrowUp, ArrowDown, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Medication Editor Component
const MedicationEditor = ({ drug, setDrug, onSave, onCancel }) => {
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave();
    setSaving(false);
  };

  const updateField = (field, value) => {
    setDrug({...drug, [field]: value});
  };

  const updateArrayItem = (field, index, value) => {
    const newArray = [...(drug[field] || [])];
    newArray[index] = value;
    setDrug({...drug, [field]: newArray});
  };

  const addArrayItem = (field) => {
    const newArray = [...(drug[field] || []), ""];
    setDrug({...drug, [field]: newArray});
  };

  const removeArrayItem = (field, index) => {
    const newArray = (drug[field] || []).filter((_, i) => i !== index);
    setDrug({...drug, [field]: newArray});
  };

  const moveArrayItem = (field, index, direction) => {
    const newArray = [...(drug[field] || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newArray.length) return;
    [newArray[index], newArray[newIndex]] = [newArray[newIndex], newArray[index]];
    setDrug({...drug, [field]: newArray});
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <h1 className="text-xl font-heading font-bold flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Medication
            </h1>
          </div>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
        {/* Basic Info */}
        <Card>
          <CardHeader><CardTitle>Basic Information</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Drug Name</Label>
                <Input value={drug.name || ''} onChange={(e) => updateField('name', e.target.value)} className="font-medium" />
              </div>
              <div className="space-y-2">
                <Label>Classification</Label>
                <Input value={drug.classification || ''} onChange={(e) => updateField('classification', e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Mechanism of Action</Label>
              <textarea value={drug.mechanism_of_action || ''} onChange={(e) => updateField('mechanism_of_action', e.target.value)} className="w-full min-h-[80px] p-2 rounded border bg-background text-sm" placeholder="How the drug works..." />
            </div>
          </CardContent>
        </Card>

        {/* Routes */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Routes of Administration</CardTitle>
              <Button size="sm" variant="outline" onClick={() => addArrayItem('route')}><Plus className="w-4 h-4 mr-1" />Add Route</Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {(drug.route || []).map((route, index) => (
                <div key={index} className="flex items-center gap-1 bg-muted rounded-lg px-2 py-1">
                  <Input value={route} onChange={(e) => updateArrayItem('route', index, e.target.value)} className="w-20 h-7 text-sm" />
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => removeArrayItem('route', index)}><X className="w-3 h-3" /></Button>
                </div>
              ))}
              {(!drug.route || drug.route.length === 0) && <p className="text-muted-foreground text-sm">No routes added</p>}
            </div>
          </CardContent>
        </Card>

        {/* Indications */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Indications (Links to Protocols)</CardTitle>
              <Button size="sm" variant="outline" onClick={() => addArrayItem('indications')}><Plus className="w-4 h-4 mr-1" />Add Indication</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(drug.indications || []).map((indication, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex flex-col gap-1">
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveArrayItem('indications', index, -1)} disabled={index === 0}><ArrowUp className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveArrayItem('indications', index, 1)} disabled={index === (drug.indications?.length || 0) - 1}><ArrowDown className="w-3 h-3" /></Button>
                </div>
                <Input value={indication} onChange={(e) => updateArrayItem('indications', index, e.target.value)} placeholder="Protocol name" className="flex-1" />
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeArrayItem('indications', index)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            {(!drug.indications || drug.indications.length === 0) && <p className="text-muted-foreground text-sm">No indications added</p>}
          </CardContent>
        </Card>

        {/* Contraindications */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Contraindications</CardTitle>
              <Button size="sm" variant="outline" onClick={() => addArrayItem('contraindications')}><Plus className="w-4 h-4 mr-1" />Add</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(drug.contraindications || []).map((contra, index) => (
              <div key={index} className="flex items-center gap-2">
                <Input value={contra} onChange={(e) => updateArrayItem('contraindications', index, e.target.value)} className="flex-1" />
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeArrayItem('contraindications', index)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            {(!drug.contraindications || drug.contraindications.length === 0) && <p className="text-muted-foreground text-sm">No contraindications added</p>}
          </CardContent>
        </Card>

        {/* Notes */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Clinical Notes</CardTitle>
              <Button size="sm" variant="outline" onClick={() => addArrayItem('notes')}><Plus className="w-4 h-4 mr-1" />Add Note</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {(drug.notes || []).map((note, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="flex flex-col gap-1">
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveArrayItem('notes', index, -1)} disabled={index === 0}><ArrowUp className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => moveArrayItem('notes', index, 1)} disabled={index === (drug.notes?.length || 0) - 1}><ArrowDown className="w-3 h-3" /></Button>
                </div>
                <Input value={note} onChange={(e) => updateArrayItem('notes', index, e.target.value)} className="flex-1" />
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => removeArrayItem('notes', index)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            {(!drug.notes || drug.notes.length === 0) && <p className="text-muted-foreground text-sm">No notes added</p>}
          </CardContent>
        </Card>

        {/* Cautions, Adverse Reactions, Preparations, Pharmacokinetics */}
        <Card><CardHeader><CardTitle>Cautions</CardTitle></CardHeader><CardContent><textarea value={drug.cautions || ''} onChange={(e) => updateField('cautions', e.target.value)} className="w-full min-h-[80px] p-2 rounded border bg-background text-sm" /></CardContent></Card>
        <Card><CardHeader><CardTitle>Adverse Reactions</CardTitle></CardHeader><CardContent><textarea value={drug.adverse_reactions || ''} onChange={(e) => updateField('adverse_reactions', e.target.value)} className="w-full min-h-[80px] p-2 rounded border bg-background text-sm" /></CardContent></Card>
        <Card><CardHeader><CardTitle>Preparations</CardTitle></CardHeader><CardContent><textarea value={drug.preparations || ''} onChange={(e) => updateField('preparations', e.target.value)} className="w-full min-h-[60px] p-2 rounded border bg-background text-sm font-mono" /></CardContent></Card>
        <Card><CardHeader><CardTitle>Pharmacokinetics</CardTitle></CardHeader><CardContent><textarea value={drug.pharmacokinetics || ''} onChange={(e) => updateField('pharmacokinetics', e.target.value)} className="w-full min-h-[60px] p-2 rounded border bg-background text-sm font-mono" /></CardContent></Card>

        {/* Dosing Note */}
        <Card className="border-yellow-500/50 bg-yellow-500/5">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0" />
              <div>
                <h4 className="font-semibold mb-1">Dosing Information</h4>
                <p className="text-sm text-muted-foreground">Drug dosing is managed within each protocol. To update dosing, edit the relevant protocol.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4 pt-4 border-t">
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving}><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save Changes"}</Button>
        </div>
      </div>
    </div>
  );
};

// Admin Medications Management
const AdminMedications = () => {
  const [drugs, setDrugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingDrug, setEditingDrug] = useState(null);
  const { token } = useAuth();

  const fetchDrugs = async () => {
    try {
      const res = await axios.get(`${API}/drugs`);
      setDrugs(res.data);
    } catch (error) {
      toast.error("Failed to load medications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDrugs(); }, []);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete medication "${name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/drugs/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setDrugs(drugs.filter(d => d.id !== id));
      toast.success("Medication deleted");
    } catch (error) {
      toast.error("Failed to delete medication");
    }
  };

  const handleEdit = (drug) => setEditingDrug(JSON.parse(JSON.stringify(drug)));

  const handleSave = async () => {
    try {
      await axios.put(`${API}/admin/drugs/${editingDrug.id}`, editingDrug, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Medication saved!");
      setEditingDrug(null);
      fetchDrugs();
    } catch (error) {
      toast.error("Failed to save medication");
    }
  };

  const filteredDrugs = drugs.filter(d => (d.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || (d.class_name || '').toLowerCase().includes(searchQuery.toLowerCase()));

  if (loading) return <div className="p-8 text-center">Loading...</div>;
  if (editingDrug) return <MedicationEditor drug={editingDrug} setDrug={setEditingDrug} onSave={handleSave} onCancel={() => setEditingDrug(null)} />;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-xl font-heading font-bold flex items-center gap-2"><Pill className="w-5 h-5" />Manage Medications ({drugs.length})</h1>
          <Link to="/admin/dashboard"><Button variant="outline">Back to Dashboard</Button></Link>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-6"><Input placeholder="Search medications..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="max-w-md" /></div>
        <div className="grid gap-2">
          {filteredDrugs.map(drug => (
            <Card key={drug.id}>
              <CardContent className="py-4 flex items-center justify-between">
                <div>
                  <div className="font-medium">{drug.name}</div>
                  <div className="text-sm text-muted-foreground"><Badge variant="outline" className="mr-2">{drug.class_name}</Badge>Routes: {drug.route?.join(", ")}</div>
                </div>
                <div className="flex gap-2">
                  <Link to={`/medication/${drug.id}`}><Button variant="outline" size="sm">View</Button></Link>
                  <Button variant="secondary" size="sm" onClick={() => handleEdit(drug)}><Edit className="w-4 h-4 mr-1" />Edit</Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(drug.id, drug.name)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminMedications;
